"use client"

import { useState, useMemo } from "react"
import { usePlayback } from "./usePlayback"
import { PlaybackControls } from "./PlaybackControls"
import type { StackQueueStep } from "./types"

interface StackQueueVizProps {
  initialItems?: (number | string)[]
  steps: StackQueueStep[]
  variant: "stack" | "queue" | "deque"
  width?: number
  height?: number
}

// Layout constants
const STACK_CELL_W = 80
const STACK_CELL_H = 40
const QUEUE_CELL_W = 60
const QUEUE_CELL_H = 40
const GAP = 4

const GREEN = "#22c55e"
const RED = "#ef4444"
const AMBER = "#f59e0b"
const DEFAULT_FILL = "#ffffff"
const DEFAULT_HIGHLIGHT = "#3b82f6"

interface ReplayResult {
  items: (number | string)[]
  highlights: Map<number, string>
  removedItem: { value: number | string; index: number } | null
}

function replaySteps(
  initialItems: (number | string)[],
  steps: StackQueueStep[],
  upTo: number
): ReplayResult {
  const items = [...initialItems]
  const highlights = new Map<number, string>()
  let removedItem: ReplayResult["removedItem"] = null

  for (let i = 0; i <= upTo; i++) {
    const step = steps[i]
    if (!step) continue

    // Only track removal animation on the final step
    if (i < upTo) removedItem = null

    switch (step.type) {
      case "push": {
        if (step.value != null) {
          items.push(step.value)
          highlights.clear()
          highlights.set(items.length - 1, GREEN)
        }
        break
      }

      case "pop": {
        if (items.length > 0) {
          const idx = items.length - 1
          if (i === upTo) {
            removedItem = { value: items[idx]!, index: idx }
          }
          highlights.clear()
          highlights.set(idx, RED)
          items.pop()
        }
        break
      }

      case "enqueue": {
        if (step.value != null) {
          if (step.end === "front") {
            items.unshift(step.value)
            highlights.clear()
            highlights.set(0, GREEN)
          } else {
            items.push(step.value)
            highlights.clear()
            highlights.set(items.length - 1, GREEN)
          }
        }
        break
      }

      case "dequeue": {
        if (items.length > 0) {
          const idx = step.end === "back" ? items.length - 1 : 0
          if (i === upTo) {
            removedItem = { value: items[idx]!, index: idx }
          }
          highlights.clear()
          highlights.set(idx, RED)
          if (step.end === "back") {
            items.pop()
          } else {
            items.shift()
          }
        }
        break
      }

      case "peek": {
        highlights.clear()
        if (items.length > 0) {
          const variant = step.variant ?? "stack"
          const idx = variant === "stack" ? items.length - 1 : 0
          highlights.set(idx, AMBER)
        }
        break
      }

      case "highlight": {
        if (step.position != null && step.position < items.length) {
          highlights.set(step.position, DEFAULT_HIGHLIGHT)
        }
        break
      }

      case "reset": {
        highlights.clear()
        removedItem = null
        break
      }
    }
  }

  return { items, highlights, removedItem }
}

export function StackQueueViz({
  initialItems = [],
  steps,
  variant,
  width = 400,
  height = 300,
}: StackQueueVizProps) {
  const {
    stepIndex,
    isPlaying,
    atStart,
    atEnd,
    stepForward,
    stepBack,
    play,
    pause,
    reset,
  } = usePlayback(steps.length)

  const { items, highlights, removedItem } = useMemo(
    () =>
      stepIndex < 0
        ? { items: [...initialItems], highlights: new Map<number, string>(), removedItem: null }
        : replaySteps(initialItems, steps, stepIndex),
    [initialItems, steps, stepIndex]
  )

  const currentStep = stepIndex >= 0 ? steps[stepIndex] : undefined
  const isStack = variant === "stack"

  return (
    <div className="flex flex-col gap-3">
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="rounded-lg border border-border bg-background"
      >
        {isStack ? (
          <StackLayout
            items={items}
            highlights={highlights}
            width={width}
            height={height}
          />
        ) : (
          <QueueLayout
            items={items}
            highlights={highlights}
            width={width}
            height={height}
            isDeque={variant === "deque"}
          />
        )}
      </svg>

      <PlaybackControls
        message={currentStep?.message ?? ""}
        isPlaying={isPlaying}
        atStart={atStart}
        atEnd={atEnd}
        stepIndex={stepIndex}
        totalSteps={steps.length}
        onStepBack={stepBack}
        onStepForward={stepForward}
        onPlay={play}
        onPause={pause}
        onReset={reset}
      />
    </div>
  )
}

function StackLayout({
  items,
  highlights,
  width,
  height,
}: {
  items: (number | string)[]
  highlights: Map<number, string>
  width: number
  height: number
}) {
  const totalH = items.length * (STACK_CELL_H + GAP) - (items.length > 0 ? GAP : 0)
  const originX = (width - STACK_CELL_W) / 2
  const bottomY = height - 20
  const bracketX = originX - 12

  return (
    <g>
      {/* Vertical bracket on the left */}
      <line
        x1={bracketX} y1={bottomY}
        x2={bracketX} y2={bottomY - Math.max(totalH, STACK_CELL_H)}
        stroke="#9ca3af" strokeWidth={2}
      />
      <line
        x1={bracketX} y1={bottomY}
        x2={bracketX + 6} y2={bottomY}
        stroke="#9ca3af" strokeWidth={2}
      />
      <line
        x1={bracketX} y1={bottomY - Math.max(totalH, STACK_CELL_H)}
        x2={bracketX + 6} y2={bottomY - Math.max(totalH, STACK_CELL_H)}
        stroke="#9ca3af" strokeWidth={2}
      />

      {items.map((value, i) => {
        // i=0 is bottom of stack, last element is top
        const y = bottomY - (i + 1) * (STACK_CELL_H + GAP) + GAP
        const fill = highlights.get(i) ?? DEFAULT_FILL
        const isHighlighted = highlights.has(i)
        const textColor = isHighlighted ? "#ffffff" : "#1f2937"

        return (
          <g
            key={`${i}-${value}`}
            style={{
              transition: "all 400ms ease",
              transform: `translate(${originX}px, ${y}px)`,
              opacity: 1,
            }}
          >
            <rect
              width={STACK_CELL_W}
              height={STACK_CELL_H}
              rx={4}
              ry={4}
              stroke="#d1d5db"
              strokeWidth={1}
              style={{ fill, transition: "fill 400ms ease" }}
            />
            <text
              x={STACK_CELL_W / 2}
              y={STACK_CELL_H / 2}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={16}
              fontWeight={600}
              fontFamily="ui-monospace, monospace"
              fill={textColor}
              style={{ transition: "fill 400ms ease" }}
            >
              {value}
            </text>
          </g>
        )
      })}

      {/* TOP label above topmost element */}
      {items.length > 0 && (
        <text
          x={originX + STACK_CELL_W / 2}
          y={bottomY - items.length * (STACK_CELL_H + GAP) + GAP - 10}
          textAnchor="middle"
          fontSize={11}
          fontWeight={700}
          fontFamily="ui-monospace, monospace"
          fill="#6b7280"
          style={{ transition: "all 400ms ease" }}
        >
          TOP
        </text>
      )}
    </g>
  )
}

function QueueLayout({
  items,
  highlights,
  width,
  height,
  isDeque,
}: {
  items: (number | string)[]
  highlights: Map<number, string>
  width: number
  height: number
  isDeque: boolean
}) {
  const totalW = items.length * (QUEUE_CELL_W + GAP) - (items.length > 0 ? GAP : 0)
  const originX = (width - totalW) / 2
  const originY = (height - QUEUE_CELL_H) / 2

  return (
    <g>
      {items.map((value, i) => {
        const x = originX + i * (QUEUE_CELL_W + GAP)
        const fill = highlights.get(i) ?? DEFAULT_FILL
        const isHighlighted = highlights.has(i)
        const textColor = isHighlighted ? "#ffffff" : "#1f2937"

        return (
          <g
            key={`${i}-${value}`}
            style={{
              transition: "all 400ms ease",
              transform: `translate(${x}px, ${originY}px)`,
              opacity: 1,
            }}
          >
            <rect
              width={QUEUE_CELL_W}
              height={QUEUE_CELL_H}
              rx={4}
              ry={4}
              stroke="#d1d5db"
              strokeWidth={1}
              style={{ fill, transition: "fill 400ms ease" }}
            />
            <text
              x={QUEUE_CELL_W / 2}
              y={QUEUE_CELL_H / 2}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={16}
              fontWeight={600}
              fontFamily="ui-monospace, monospace"
              fill={textColor}
              style={{ transition: "fill 400ms ease" }}
            >
              {value}
            </text>
          </g>
        )
      })}

      {/* FRONT label */}
      {items.length > 0 && (
        <text
          x={originX + QUEUE_CELL_W / 2}
          y={originY - 10}
          textAnchor="middle"
          fontSize={11}
          fontWeight={700}
          fontFamily="ui-monospace, monospace"
          fill="#6b7280"
        >
          FRONT
        </text>
      )}

      {/* BACK label */}
      {items.length > 0 && (
        <text
          x={originX + (items.length - 1) * (QUEUE_CELL_W + GAP) + QUEUE_CELL_W / 2}
          y={originY - 10}
          textAnchor="middle"
          fontSize={11}
          fontWeight={700}
          fontFamily="ui-monospace, monospace"
          fill="#6b7280"
        >
          BACK
        </text>
      )}

      {/* Deque: additional labels below showing both ends can push/pop */}
      {isDeque && items.length > 0 && (
        <>
          <text
            x={originX + QUEUE_CELL_W / 2}
            y={originY + QUEUE_CELL_H + 16}
            textAnchor="middle"
            fontSize={9}
            fontFamily="ui-monospace, monospace"
            fill="#9ca3af"
          >
            push/pop
          </text>
          <text
            x={originX + (items.length - 1) * (QUEUE_CELL_W + GAP) + QUEUE_CELL_W / 2}
            y={originY + QUEUE_CELL_H + 16}
            textAnchor="middle"
            fontSize={9}
            fontFamily="ui-monospace, monospace"
            fill="#9ca3af"
          >
            push/pop
          </text>
        </>
      )}
    </g>
  )
}
